import SubmitButton from 'components/cart-item-editor/SubmitButton';
import { useItemSheet } from 'hooks/use-item-sheet';
import { observer } from 'mobx-react';
import { useEffect, useState, useRef } from 'react';
import { useStores } from 'hooks/use-stores';
import { getUpsellsFromMenuItems } from 'stores/utils';
import Menu from 'models/Menu';
import useProgrammaticUpsells from 'DynamicValues/FeatureFlags/useProgrammaticUpsells';
import { managedFeatures } from 'DynamicValues/DynamicValuesProvider';
import {
  CartCloseButton,
  CartImage,
  CartModal,
  CloseOnTouch,
  Container,
  FixedHeader,
  ImageWrapper,
  Main,
} from '../styles';
import ItemDetailsContent from './ItemDetailsContent';

function CartItemModalContent({ isNew, handleSubmit }: { isNew: boolean; handleSubmit: () => void }) {
  const { checkoutStore, uiState, locationStore, menuDataStore } = useStores();
  const { isMobile, menuItemCardRef } = uiState;
  const { selectedCart } = checkoutStore;
  const {
    customer: { getEnabledFeatures },
  } = locationStore;
  const { submitPressed, cartItem, closeModal, displayedItem, addHTMLElementError } = useItemSheet();
  const [showFixedHeader, setShowFixedHeader] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const showProgrammaticUpsells = useProgrammaticUpsells() || getEnabledFeatures(managedFeatures.programmaticUpsells);
  const { menuItem } = cartItem;
  let fulfillableItems;
  if (menuDataStore.menus) {
    fulfillableItems =
      (menuDataStore?.menus?.find((menu) => menu.id === cartItem.menu_id) as Menu)?.fulfillableItems ?? [];
  }
  useEffect(() => {
    uiState.lockBodyScroll();
    return () => uiState.unlockBodyScroll();
  }, [uiState]);

  const handleImageLoadError = () => setImageLoadError(true);

  let recommendedItems;
  const currentMenu = menuDataStore.menus?.find((menu) => menu.id === cartItem.menu_id);
  const allMenuItems = currentMenu?.headings.flatMap((heading) => heading.items);
  const currentItem = allMenuItems?.find((item) => item.id === menuItem.id);
  const currentItemHeadingId = currentItem?.menu_heading_id;
  const menuHeading = currentMenu?.headings.find((heading) => heading.id === currentItemHeadingId);
  if (fulfillableItems) {
    recommendedItems = getUpsellsFromMenuItems({
      fulfillableItems,
      menuItem,
      menuHeading,
      showProgrammaticUpsells,
      fromCart: false,
      menu: cartItem.menu_id,
    });
  }

  const hasImage = Boolean(!imageLoadError && menuItem.imageUrl);

  const mobileWidth = '100%';
  const desktopWidth = hasImage ? 1000 : 500;
  const width = isMobile ? mobileWidth : desktopWidth;
  const isViewingCartItem = cartItem === displayedItem;

  const modalTitleRef = useRef<HTMLDivElement>(null);
  // A11Y: On render, focus the modal's title for SRs. On unmount, focus the card that opened the modal
  useEffect(() => {
    modalTitleRef?.current?.focus();
    return () => {
      menuItemCardRef?.current?.focus();
    };
  }, []);

  return (
    <CartModal
      centered
      closable={false}
      footer={null}
      onCancel={closeModal}
      visible
      width={width}
      data-testid="cart-item-modal"
    >
      {/* Fixed Header (appears when the `CartItemModalHeader` scrolls out of view) */}
      <FixedHeader hasImage={hasImage} visible={showFixedHeader}>
        <CartCloseButton onClick={closeModal} />
      </FixedHeader>
      {/* Allow users to close the modal by touching the gap above it. */}
      {isMobile && <CloseOnTouch onClick={closeModal} />}
      {/* Main Content */}
      <Container
        data-testid="cart-item-modal-content"
        role="dialog"
        tabIndex={0}
        aria-label={menuItem.name_for_customer}
      >
        <CartCloseButton onClick={closeModal} />
        {hasImage && (
          <ImageWrapper>
            <CartImage
              src={`${String(process.env.REACT_APP_STATIC_ROOT)}${menuItem.imageUrl}`}
              onError={handleImageLoadError}
              alt={menuItem.name_for_customer}
            />
          </ImageWrapper>
        )}
        <Main>
          <ItemDetailsContent
            hasImage={hasImage}
            isViewingCartItem={isViewingCartItem}
            isNew={isNew}
            submitPressed={submitPressed}
            addHTMLElementError={addHTMLElementError}
            recommendedItems={recommendedItems}
            menuItem={menuItem}
            locationStore={locationStore}
            checkoutStore={checkoutStore}
            cartItem={cartItem}
            selectedCart={selectedCart}
            setShowFixedHeader={setShowFixedHeader}
            isMobile={isMobile}
            modalTitleRef={modalTitleRef}
          />

          {locationStore.enable_checkout && checkoutStore.isAllowedToEditItem(cartItem) && (
            <SubmitButton handleClick={handleSubmit} cartItem={cartItem} currItem={displayedItem} isNew={isNew} />
          )}
        </Main>
      </Container>
    </CartModal>
  );
}

export default observer(CartItemModalContent);
